import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useDeleteFloor,
  useDeleteRoom,
  useUpdateRoom,
} from 'src/Hooks/Mutations/useCartographyMutations';
import { BuildingDto, FloorDto, RoomDto } from 'src/types/cartography.types';

import { FloorDrawer } from '../components/FloorDrawer';
import { RoomDrawer } from '../components/RoomDrawer';
import { isSleepingRoomType } from '../referential';

export interface FloorGroup {
  floor: FloorDto | null;
  rooms: RoomDto[];
}

interface UseBuildingContent {
  hasFloors: boolean;
  groups: FloorGroup[];
  enSuiteLabelOf: (room: RoomDto) => string | undefined;
  openCreateFloor: () => void;
  openEditFloor: (floor: FloorDto) => void;
  openCreateRoom: (floorId: string | null) => void;
  handleEditRoom: (room: RoomDto) => void;
  handleToggleOutOfService: (room: RoomDto) => void;
  handleDeleteRoom: (room: RoomDto) => void;
  handleDeleteFloor: (floor: FloorDto) => void;
  drawers: React.ReactNode;
}

// Shared building-level state used by both Home v2 building views. Owns the FloorDrawer
// and RoomDrawer instances, the mutations, and the confirmation dialogs, so both the
// house view and the table view can call the same handlers.
// `order` controls floor stacking: 'desc' (default) is the house drawing where the attic
// stays on top; 'asc' is the table view which reads bottom-up from the ground floor.
// `onRoomCreated` lets the parent react once a new room is persisted (used to auto-open
// the sleeping room detail panel with its bed form unfolded).
const useBuildingContent = (
  building: BuildingDto,
  order: 'asc' | 'desc' = 'desc',
  onRoomCreated?: (room: RoomDto) => void
): UseBuildingContent => {
  const { t } = useTranslation();

  const [floorDrawerOpen, setFloorDrawerOpen] = useState(false);
  const [editingFloor, setEditingFloor] = useState<FloorDto | undefined>();

  const [roomDrawerOpen, setRoomDrawerOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<RoomDto | undefined>();
  const [defaultFloorIdForCreate, setDefaultFloorIdForCreate] = useState<string | null>(null);

  const { mutate: deleteFloor } = useDeleteFloor();
  const { mutate: deleteRoom } = useDeleteRoom();
  const { mutate: updateRoom } = useUpdateRoom();

  const hasFloors = building.floors.length > 0;

  const groups: FloorGroup[] = useMemo(() => {
    if (!hasFloors) {
      return [{ floor: null, rooms: building.rooms }];
    }

    const byFloorId = new Map<string, RoomDto[]>();
    const noFloor: RoomDto[] = [];

    for (const room of building.rooms) {
      if (room.floorId) {
        const arr = byFloorId.get(room.floorId) ?? [];
        arr.push(room);
        byFloorId.set(room.floorId, arr);
      } else {
        noFloor.push(room);
      }
    }

    // Tie-break on `position` so two floors sharing a level never flip between reloads.
    const direction = order === 'asc' ? 1 : -1;
    const sortedFloors = [...building.floors].sort(
      (a, b) => direction * (a.level - b.level) || direction * (a.position - b.position)
    );
    const grouped: FloorGroup[] = sortedFloors.map(floor => ({
      floor,
      rooms: byFloorId.get(floor.id) ?? [],
    }));

    // "Sans niveau" sits at the end regardless of direction.
    if (noFloor.length > 0) {
      grouped.push({ floor: null, rooms: noFloor });
    }

    return grouped;
  }, [building.floors, building.rooms, hasFloors, order]);

  const roomNameById = new Map(building.rooms.map(room => [room.id, room.name]));
  const washroomNamesByBedroomId = building.rooms.reduce((acc, room) => {
    if (!room.servedRoomId) return acc;
    acc.set(room.servedRoomId, [...(acc.get(room.servedRoomId) ?? []), room.name]);
    return acc;
  }, new Map<string, string[]>());

  const enSuiteLabelOf = (room: RoomDto): string | undefined => {
    if (room.servedRoomId) return roomNameById.get(room.servedRoomId);
    return washroomNamesByBedroomId.get(room.id)?.join(', ');
  };

  const bedrooms = building.rooms.filter(room => isSleepingRoomType(room.type));

  const openCreateFloor = () => {
    setEditingFloor(undefined);
    setFloorDrawerOpen(true);
  };

  const openEditFloor = (floor: FloorDto) => {
    setEditingFloor(floor);
    setFloorDrawerOpen(true);
  };

  const openCreateRoom = (floorId: string | null) => {
    setEditingRoom(undefined);
    setDefaultFloorIdForCreate(floorId);
    setRoomDrawerOpen(true);
  };

  const handleEditRoom = (room: RoomDto) => {
    setEditingRoom(room);
    setDefaultFloorIdForCreate(null);
    setRoomDrawerOpen(true);
  };

  const handleToggleOutOfService = (room: RoomDto) => {
    updateRoom({
      roomId: room.id,
      payload: { isOutOfService: !room.isOutOfService },
    });
  };

  const handleDeleteRoom = (room: RoomDto) => {
    const bedCount = room.beds.length;
    modals.openConfirmModal({
      title: t('admin.structure.rooms.deleteTitle', 'Supprimer cette pièce ?'),
      children: (
        <Text size="sm">
          {bedCount > 0
            ? t('admin.structure.rooms.deleteConfirmDetailed', {
                name: room.name,
                count: bedCount,
                before: building.capacity,
                after: building.capacity - room.capacity,
                defaultValue:
                  '« {{name}} » et ses {{count}} couchages seront supprimés : la capacité du bâtiment passe de {{before}} à {{after}}.',
              })
            : t('admin.structure.rooms.deleteConfirmSimple', {
                name: room.name,
                defaultValue: '« {{name}} » sera supprimée.',
              })}
        </Text>
      ),
      labels: { confirm: t('common.delete', 'Supprimer'), cancel: t('common.cancel', 'Annuler') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteRoom(room.id),
    });
  };

  const handleDeleteFloor = (floor: FloorDto) => {
    modals.openConfirmModal({
      title: t('admin.structure.floors.deleteTitle', 'Supprimer ce niveau ?'),
      children: (
        <Text size="sm">
          {t(
            'admin.structure.floors.deleteConfirm',
            'Les pièces qui y étaient rattachées resteront, mais détachées de tout niveau.'
          )}
        </Text>
      ),
      labels: { confirm: t('common.delete', 'Supprimer'), cancel: t('common.cancel', 'Annuler') },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteFloor(floor.id),
    });
  };

  const drawers = (
    <>
      <FloorDrawer
        opened={floorDrawerOpen}
        buildingId={building.id}
        floor={editingFloor}
        onClose={() => setFloorDrawerOpen(false)}
      />
      <RoomDrawer
        opened={roomDrawerOpen}
        buildingId={building.id}
        floors={building.floors}
        defaultFloorId={defaultFloorIdForCreate}
        room={editingRoom}
        bedrooms={bedrooms}
        onClose={() => setRoomDrawerOpen(false)}
        onRoomCreated={onRoomCreated}
      />
    </>
  );

  return {
    hasFloors,
    groups,
    enSuiteLabelOf,
    openCreateFloor,
    openEditFloor,
    openCreateRoom,
    handleEditRoom,
    handleToggleOutOfService,
    handleDeleteRoom,
    handleDeleteFloor,
    drawers,
  };
};

export default useBuildingContent;
